/**
 * Défis live privés (migration 0650) : création, lecture par code, départ par
 * l'hôte, présence, classement, défis reçus. Le jeu lui-même réutilise le
 * player Live School (`useLiveQuiz` avec l'identifiant du défi).
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

// RPC postérieures aux types générés → client générique.
const db = supabase as unknown as SupabaseClient;

/** Origine FIXE des liens partagés (jamais `capacitor://` sur natif). */
export const PRIVATE_LIVE_ORIGIN = 'https://app.jeraisonne.fr';
/** Toutes les questions sur la feuille de brouillon (0704) : 5 au plus (miroir serveur). */
export const PRIVATE_LIVE_MAX_QUESTIONS = 5;
/** Miroir de `_shared/private-live.ts`. */
export const JOIN_CODE_RE = /^[A-HJKMNP-Z2-9]{8}$/;

export type PrivateLiveDifficulty = 'easy' | 'medium' | 'hard';

/**
 * Miroir de `_shared/private-live.ts` : mélangé / alterné / questions rapides
 * (défis d'avant 0704) ; `sheet` = toutes les questions sur la feuille (le seul créé).
 */
export type PrivateLiveFormat = 'mixed' | 'alternate' | 'quick' | 'sheet';

/** Durée d'une question à brouillon : 1 à 3 min, par pas de 30 s (miroir serveur). */
export const SHEET_MIN_MS = 60_000;
export const SHEET_MAX_MS = 180_000;
export const SHEET_STEP_MS = 30_000;
export const SHEET_DEFAULT_MS = 120_000;
/** Défi lancé depuis un exercice : 2 min 30 par question (le temps de refaire l'exercice sur la feuille). */
export const EXERCISE_CHALLENGE_SHEET_MS = 150_000;

/**
 * Sur quoi portent les questions (0722, miroir serveur) : programme du niveau, un pack,
 * des SKILLCARDS choisies ou un exercice (ses questions, dans l'ordre). Sans accès payant,
 * les questions viennent des essais gratuits des cartes (et des cartes achetées ou données).
 */
export type PrivateLiveScope = 'programme' | 'pack' | 'cards' | 'exercise';
/** SKILLCARDS choisies : 20 au plus (miroir serveur). */
export const PRIVATE_LIVE_MAX_CARDS = 20;

export interface PrivateLiveSettings {
  level: string;
  subject: string;
  pack_id: string | null;
  scope?: PrivateLiveScope;
  card_ids?: string[];
  exercise_id?: string | null;
  count: number;
  format: PrivateLiveFormat;
  alternate: boolean;
  final_sheet: boolean;
  sheet_ms: number;
  progressive: boolean;
  difficulty: PrivateLiveDifficulty;
}

/** Format d'un défi (accepte les anciens réglages `{alternate}`). PUR. */
export function privateLiveFormatOf(settings: Partial<PrivateLiveSettings>): PrivateLiveFormat {
  const f = settings.format;
  if (f === 'mixed' || f === 'alternate' || f === 'quick' || f === 'sheet') return f;
  return settings.alternate ? 'alternate' : 'mixed';
}

/** Durée bornée à 1-3 min, arrondie à 30 s. PUR. */
export function clampSheetMs(ms: number): number {
  const n = Number.isFinite(ms) ? Math.round(ms / SHEET_STEP_MS) * SHEET_STEP_MS : SHEET_DEFAULT_MS;
  return Math.min(SHEET_MAX_MS, Math.max(SHEET_MIN_MS, n));
}

/** « 1 min », « 2 min 30 ». PUR. */
export function sheetDurationLabel(ms: number): string {
  const total = Math.round(clampSheetMs(ms) / 1000);
  const min = Math.floor(total / 60);
  const sec = total % 60;
  return sec ? `${min} min ${sec}` : `${min} min`;
}

export interface PrivateLivePerson {
  id: string;
  display_name: string | null;
  avatar_url: string | null;
}

export interface PrivateLivePlayer {
  name: string;
  avatar_url: string | null;
  is_guest: boolean;
  is_host: boolean;
  is_me: boolean;
  connected: boolean;
}

/** Réponse de `get_private_live(code)`. */
export interface PrivateLive {
  server_time: number;
  id: string;
  join_code: string;
  title: string | null;
  level: string;
  subject: string | null;
  status: 'scheduled' | 'live' | 'finished' | 'snapshotted';
  awaiting_host: boolean;
  starts_at: string | null;
  step_count: number;
  step_duration_ms: number;
  settings: Partial<PrivateLiveSettings>;
  /** Cover choisie (chemin du bucket `live-covers`, migration 0658), sinon null. */
  cover_path?: string | null;
  host: PrivateLivePerson | null;
  registered: number;
  connected: number;
  /** Plafond de joueurs, organisateur non compté (0728) ; null = défi d'avant, sans plafond. */
  max_players?: number | null;
  /** Joueurs inscrits sans l'organisateur (0728). */
  players_count?: number;
  is_host: boolean;
  joined: boolean;
  players: PrivateLivePlayer[];
}

// ── Paywall (0728, miroir SQL `private_live_host_plan`) ─────────────────────

/** Organisateur gratuit : 1 défi par jour, 3 joueurs ; payant : 50 joueurs, illimité. */
export const PRIVATE_LIVE_FREE_MAX_PLAYERS = 3;
export const PRIVATE_LIVE_PREMIUM_MAX_PLAYERS = 50;

/** Réponse de `private_live_quota_for(réglages)` (0729). */
export interface PrivateLiveQuota {
  premium: boolean;
  reason: string | null;
  max_players: number;
  daily_limit: number | null;
  created_today: number;
  can_create: boolean;
  next_at: string;
}

/** Le défi est complet pour ce visiteur (ni organisateur, ni déjà inscrit). PUR. */
export function isPrivateLiveFull(
  live: Pick<PrivateLive, 'max_players' | 'players_count' | 'is_host' | 'joined'>,
): boolean {
  if (live.is_host || live.joined || !live.max_players) return false;
  return (live.players_count ?? 0) >= live.max_players;
}

/** « 2/3 joueurs », ou null sans plafond. PUR. */
export function playersCapLabel(
  live: Pick<PrivateLive, 'max_players' | 'players_count'>,
): string | null {
  if (!live.max_players) return null;
  return `${live.players_count ?? 0}/${live.max_players} joueur${live.max_players > 1 ? 's' : ''}`;
}

/** Ligne de l'offre affichée à la création. PUR. */
export function quotaLine(q: PrivateLiveQuota): string {
  if (q.reason === 'owned')
    return `Tu as les SKILLCARDS de ce défi : jusqu’à ${q.max_players} joueurs, défis illimités.`;
  if (q.premium) return `Jusqu’à ${q.max_players} joueurs, défis illimités.`;
  return q.can_create
    ? `Offre gratuite : 1 défi par jour, ${q.max_players} joueurs maximum.`
    : 'Tu as déjà créé ton défi gratuit aujourd’hui. Le prochain, c’est demain.';
}

/** Élément de `get_my_private_lives()` (onglet Live School, Feed). */
export interface MyPrivateLive {
  id: string;
  join_code: string;
  title: string | null;
  level: string;
  subject: string | null;
  step_count: number;
  status: PrivateLive['status'];
  awaiting_host: boolean;
  starts_at: string | null;
  created_at: string;
  invited_at: string;
  hosted: boolean;
  /** Cover choisie (migration 0658), sinon null. */
  cover_path?: string | null;
  /** Réglages du défi (résumé « 6 questions · 1 avec brouillon »), migration 0658. */
  settings?: Partial<PrivateLiveSettings>;
  host: PrivateLivePerson | null;
  registered: number;
  /** L'appelant est dans le défi (migration 0733 ; absent avant). */
  joined?: boolean;
}

export interface PrivateBoardRow {
  rank: number;
  name: string;
  avatar_url: string | null;
  total_score: number;
  correct_count: number;
  /** Temps de réponse moyen (ms), migration 0655. */
  avg_time_ms?: number | null;
  is_guest: boolean;
  is_me: boolean;
}

export interface PrivateBoard {
  rows: PrivateBoardRow[];
  me: { rank: number; total: number } | null;
}

// ── Règles pures ────────────────────────────────────────────────────────────

/**
 * Le défi peut-il avoir une question avec feuille de brouillon ? « Mélangé »
 * peut en tirer : on le compte. PUR.
 */
export function privateLiveMayHaveSheet(
  settings: Partial<PrivateLiveSettings>,
  stepCount: number,
): boolean {
  const format = privateLiveFormatOf(settings);
  if (format === 'quick') return settings.final_sheet === true;
  if (format === 'alternate') return stepCount >= 2;
  return true;
}

/**
 * Proposer l'entraînement à la feuille dans le salon : défi avec brouillon, et
 * joueur invité ou qui ne l'a jamais ouverte sur cet appareil. PUR.
 */
export function shouldOfferScratchPractice(opts: {
  settings: Partial<PrivateLiveSettings>;
  stepCount: number;
  isGuest: boolean;
  seenTutorial: boolean;
}): boolean {
  if (!privateLiveMayHaveSheet(opts.settings, opts.stepCount)) return false;
  return opts.isGuest || !opts.seenTutorial;
}

/** Lien à partager : `https://app.jeraisonne.fr/defi-live/ABCDEFGH`. */
export function privateLiveUrl(code: string): string {
  return `${PRIVATE_LIVE_ORIGIN}/defi-live/${code}`;
}

/** Page d'aperçu du site, servie aux robots de partage (apps/app/vercel.json). */
export const PRIVATE_LIVE_PREVIEW_ORIGIN = 'https://www.jeraisonne.fr';

/** URLs à pré-générer pour l'aperçu d'un lien de défi (image OG + page du site). PUR. */
export function privateLivePreviewUrls(code: string, supabaseUrl: string | undefined): string[] {
  const urls = [`${PRIVATE_LIVE_PREVIEW_ORIGIN}/defi-live/${code}`];
  const base = supabaseUrl?.replace(/\/$/, '');
  if (base) urls.unshift(`${base}/functions/v1/og-private-live?code=${encodeURIComponent(code)}`);
  return urls;
}

/**
 * Pré-génère l'aperçu du lien (image + page) sans l'attendre : quand l'organisateur
 * partage, WhatsApp & co trouvent une image déjà prête. Silencieux en cas d'échec.
 */
export function warmPrivateLivePreview(code: string): void {
  if (typeof fetch !== 'function') return;
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  for (const url of privateLivePreviewUrls(code, supabaseUrl)) {
    try {
      void fetch(url, { mode: 'no-cors', keepalive: true }).catch(() => undefined);
    } catch {
      /* aperçu facultatif */
    }
  }
}

/** Code saisi (minuscules, tiret, espaces) → code canonique ou null. */
export function normalizeJoinCode(raw: string): string | null {
  const code = raw.toUpperCase().replace(/[\s-]/g, '');
  return JOIN_CODE_RE.test(code) ? code : null;
}

/** « ABCD-EFGH » : plus lisible à dicter. */
export function formatJoinCode(code: string): string {
  return code.length === 8 ? `${code.slice(0, 4)}-${code.slice(4)}` : code;
}

/** Prénom d'un invité : 1 à 30 caractères (miroir serveur), sinon null. */
export function normalizeGuestName(raw: string): string | null {
  const name = Array.from(raw)
    .filter((ch) => {
      const c = ch.charCodeAt(0);
      return c >= 32 && c !== 127;
    })
    .join('')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 30);
  return name.trim().length > 0 ? name.trim() : null;
}

export type PrivateLiveStage = 'awaiting' | 'scheduled' | 'running' | 'finished';

/** Où en est le défi, vu du salon. */
export function privateLiveStage(
  live: Pick<PrivateLive, 'status' | 'awaiting_host' | 'starts_at'>,
  serverNow: number,
): PrivateLiveStage {
  if (live.status === 'finished' || live.status === 'snapshotted') return 'finished';
  if (live.awaiting_host || !live.starts_at) return 'awaiting';
  if (live.status === 'live' || Date.parse(live.starts_at) <= serverNow) return 'running';
  return 'scheduled';
}

const DIFFICULTY_LABEL: Record<PrivateLiveDifficulty, string> = {
  easy: 'Facile',
  medium: 'Moyenne',
  hard: 'Difficile',
};

/** « 6 questions rapides + 1 avec brouillon (2 min) · difficulté progressive ». */
export function privateLiveSummary(
  settings: Partial<PrivateLiveSettings>,
  stepCount: number,
): string {
  const format = privateLiveFormatOf(settings);
  const plural = (n: number) => (n > 1 ? 's' : '');
  const sheet = sheetDurationLabel(settings.sheet_ms ?? SHEET_DEFAULT_MS);
  const parts: string[] = [];
  if (format === 'quick') {
    const quick = settings.final_sheet ? Math.max(0, stepCount - 1) : stepCount;
    parts.push(
      settings.final_sheet
        ? `${quick} question${plural(quick)} rapide${plural(quick)} + 1 avec brouillon (${sheet})`
        : `${quick} question${plural(quick)} rapide${plural(quick)}`,
    );
  } else {
    parts.push(`${stepCount} question${plural(stepCount)}`);
    if (format === 'alternate') parts.push(`cours et brouillon (${sheet})`);
  }
  parts.push(
    settings.progressive === false
      ? `difficulté ${DIFFICULTY_LABEL[settings.difficulty ?? 'medium'].toLowerCase()}`
      : 'difficulté progressive',
  );
  return parts.join(' · ');
}

/** Heure programmée : au moins 1 min, au plus 30 jours (miroir serveur). */
export function scheduleError(atMs: number, now: number): string | null {
  if (!Number.isFinite(atMs)) return 'Choisis une date et une heure.';
  if (atMs < now + 60_000) return 'Choisis une heure dans au moins une minute.';
  if (atMs > now + 30 * 86_400_000) return 'Choisis une date dans les 30 prochains jours.';
  return null;
}

const ERRORS: Record<string, string> = {
  account_required: 'Il faut un compte pour organiser un défi.',
  rate_limited: 'Tu as déjà créé beaucoup de défis aujourd’hui. Réessaie demain.',
  daily_limit_free:
    'Tu as déjà créé ton défi gratuit aujourd’hui. Reviens demain, ou passe à l’accès complet pour des défis illimités.',
  private_live_full: 'Ce défi est complet.',
  no_questions:
    'Pas encore assez de questions de SKILLCARDS pour ce choix. Essaie un autre pack ou une autre matière.',
  bad_cards: 'Choisis entre 1 et 20 SKILLCARDS.',
  bad_invitees: 'Ce compte n’est pas relié au tien : relie-le d’abord depuis « Mes enfants ».',
  bad_exercise: 'Choisis un exercice.',
  exercise_locked:
    'Cet exercice est réservé aux SKILLCARDS débloquées. Choisis l’essai gratuit, ou débloque la carte.',
  exercise_not_skillcard: 'Cet exercice n’est relié à aucune SKILLCARD.',
  bad_level: 'Choisis un niveau.',
  bad_subject: 'Choisis une matière.',
  bad_count: 'Choisis entre 1 et 10 questions.',
  bad_format: 'Choisis le format du défi.',
  bad_sheet_ms: 'Le temps d’une question à brouillon va de 1 à 3 minutes.',
  start_too_soon: 'Choisis une heure dans au moins une minute.',
  start_too_far: 'Choisis une date dans les 30 prochains jours.',
  already_started: 'Le défi a déjà commencé.',
  not_host: 'Seul l’organisateur peut lancer le défi.',
  host_cannot_leave: 'Tu organises ce défi : annule-le plutôt.',
  not_found: 'Ce défi n’existe plus.',
};

/** Message lisible d'une erreur d'edge function (code dans le corps JSON). */
export function privateLiveErrorMessage(code: string | null | undefined): string {
  return (code && ERRORS[code]) || 'Une erreur est survenue. Réessaie.';
}

async function invokeOrThrow<T>(name: string, body: unknown): Promise<T> {
  const { data, error } = await supabase.functions.invoke<T>(name, { body: body as object });
  if (error) {
    let code: string | null = null;
    try {
      const ctx = (error as { context?: Response }).context;
      code = ((await ctx?.json()) as { error?: string } | undefined)?.error ?? null;
    } catch {
      /* corps illisible */
    }
    throw new Error(code ?? 'unknown');
  }
  return data as T;
}

// ── Hooks ───────────────────────────────────────────────────────────────────

export const privateLiveKey = (code: string) => ['private-live', code] as const;
export const myPrivateLivesKey = ['my-private-lives'] as const;

/**
 * Défi par son code. Rafraîchi toutes les 5 s tant qu'il n'est pas terminé
 * (salon). `poll: false` : lecture seule du cache (cover, titre pendant le jeu),
 * sans requête toutes les 5 s.
 */
export function usePrivateLive(
  code: string | null,
  userId: string | null,
  opts: { poll?: boolean } = {},
) {
  const poll = opts.poll ?? true;
  return useQuery({
    queryKey: [...privateLiveKey(code ?? ''), userId ?? ''],
    enabled: !!code,
    staleTime: poll ? 0 : 60_000,
    refetchInterval: (q) => {
      if (!poll) return false;
      const d = q.state.data as PrivateLive | null | undefined;
      return d && (d.status === 'finished' || d.status === 'snapshotted') ? false : 5_000;
    },
    queryFn: async (): Promise<PrivateLive | null> => {
      const { data, error } = await db.rpc('get_private_live', { p_code: code });
      if (error) throw new Error(error.message);
      return (data as PrivateLive | null) ?? null;
    },
  });
}

export interface CreatePrivateLiveInput extends Omit<PrivateLiveSettings, 'alternate'> {
  title?: string | null;
  start: { mode: 'manual' } | { mode: 'scheduled'; at: string };
  /** Inviter seulement ces contacts (un parent qui défie un enfant) ; absent = tous ses contacts. */
  invitees?: string[];
}

export const privateLiveQuotaKey = ['private-live-quota'] as const;

/** Ce que le serveur lit pour savoir si l'organisateur possède les SKILLCARDS du défi. */
export interface PrivateLiveQuotaSettings {
  scope: PrivateLiveScope;
  level: string;
  subject: string;
  pack_id: string | null;
  card_ids: string[];
  exercise_id: string | null;
}

/**
 * Offre de l'organisateur connecté pour le défi en préparation (null sans compte réel) :
 * payant avec un abonnement, un pass, ou toutes les SKILLCARDS du défi achetées.
 */
export function usePrivateLiveQuota(settings: PrivateLiveQuotaSettings, enabled = true) {
  return useQuery({
    queryKey: [...privateLiveQuotaKey, settings],
    enabled,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<PrivateLiveQuota | null> => {
      const { data, error } = await db.rpc('private_live_quota_for', { p_settings: settings });
      if (error) throw new Error(error.message);
      return (data as PrivateLiveQuota | null) ?? null;
    },
  });
}

export function useCreatePrivateLive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePrivateLiveInput) =>
      invokeOrThrow<{
        challenge_id: string;
        join_code: string;
        step_count: number;
        invited: number;
      }>('live-private-create', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: myPrivateLivesKey });
      void qc.invalidateQueries({ queryKey: privateLiveQuotaKey });
    },
    onError: () => void qc.invalidateQueries({ queryKey: privateLiveQuotaKey }),
  });
}

export function useStartPrivateLive(code: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      challengeId: string;
      action: 'start' | 'schedule' | 'cancel';
      at?: string;
    }) =>
      invokeOrThrow<{ ok: boolean; starts_at?: string; cancelled?: boolean }>(
        'live-private-start',
        {
          challenge_id: input.challengeId,
          action: input.action,
          at: input.at,
        },
      ),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: privateLiveKey(code) });
      void qc.invalidateQueries({ queryKey: ['live-state'] });
      void qc.invalidateQueries({ queryKey: myPrivateLivesKey });
    },
  });
}

/** Signal de présence (« connectés »). */
export async function privateLiveHeartbeat(challengeId: string): Promise<void> {
  await db.rpc('live_private_heartbeat', { p_challenge: challengeId });
}

export function usePrivateLiveBoard(challengeId: string | null, refetchMs: number | false) {
  return useQuery({
    queryKey: ['private-live-board', challengeId ?? ''],
    enabled: !!challengeId,
    refetchInterval: refetchMs,
    staleTime: 5_000,
    queryFn: async (): Promise<PrivateBoard> => {
      const { data, error } = await db.rpc('get_private_live_leaderboard', {
        p_challenge: challengeId,
      });
      if (error) throw new Error(error.message);
      return (data as PrivateBoard | null) ?? { rows: [], me: null };
    },
  });
}

/** Progrès d'un joueur pendant un défi privé (RPC `get_private_live_progress`, 0704). */
export interface PrivateLiveProgressRow {
  /** participation (clé stable) */
  key: string;
  name: string;
  avatar_url: string | null;
  is_me: boolean;
  /** HORA des réponses */
  answer_points: number;
  /** HORA des lignes de brouillon validées */
  scratch_hora: number;
  lines: number;
  last_line_at: string | null;
}

/**
 * Progrès de chaque joueur pendant le défi, relu toutes les 3 s : HORA de réponse
 * et de brouillon, nombre de lignes validées (jamais leur texte).
 */
export function usePrivateLiveProgress(challengeId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['private-live-progress', challengeId ?? ''],
    enabled: enabled && !!challengeId,
    refetchInterval: enabled ? 3_000 : false,
    staleTime: 2_000,
    queryFn: async (): Promise<PrivateLiveProgressRow[]> => {
      const { data, error } = await db.rpc('get_private_live_progress', {
        p_challenge: challengeId,
      });
      if (error) throw new Error(error.message);
      return ((data as { rows?: PrivateLiveProgressRow[] } | null)?.rows ?? []).map((r) => ({
        ...r,
        answer_points: Number(r.answer_points) || 0,
        scratch_hora: Number(r.scratch_hora) || 0,
        lines: Number(r.lines) || 0,
      }));
    },
  });
}

/**
 * Quitter un défi privé pas encore commencé (migration 0733) : la participation est retirée. L'organisateur, lui,
 * annule le défi.
 */
export function useLeavePrivateLive(code: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (challengeId: string): Promise<void> => {
      const { error } = await db.rpc('leave_private_live', { p_challenge_id: challengeId });
      if (error) {
        const known = Object.keys(ERRORS).find((k) => error.message.includes(k));
        throw new Error(known ?? 'unknown');
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: privateLiveKey(code) });
      void qc.invalidateQueries({ queryKey: myPrivateLivesKey });
    },
  });
}

/** Défis privés organisés ou reçus (30 derniers jours). */
export function useMyPrivateLives(enabled = true) {
  return useQuery({
    queryKey: myPrivateLivesKey,
    enabled,
    staleTime: 30_000,
    queryFn: async (): Promise<MyPrivateLive[]> => {
      const { data, error } = await db.rpc('get_my_private_lives', { p_limit: 20 });
      if (error) throw new Error(error.message);
      return (data as MyPrivateLive[] | null) ?? [];
    },
  });
}

/**
 * Session d'invité : réutilise la session existante, sinon crée une session
 * ANONYME (JWT `authenticated` + `is_anonymous`, jamais `anon`) avec le prénom.
 */
export async function ensureGuestSession(
  displayName: string,
  getCaptchaToken?: () => Promise<string | undefined>,
): Promise<{ userId: string } | { error: string }> {
  const { data: existing, error: existingError } = await supabase.auth.getUser();
  if (existing.user) return { userId: existing.user.id };
  // Session locale d'un compte anonyme purgé : on l'efface avant d'en recréer une.
  if (existingError) await supabase.auth.signOut({ scope: 'local' });
  const captchaToken = await getCaptchaToken?.();
  const { data, error } = await supabase.auth.signInAnonymously({
    options: { data: { display_name: displayName }, captchaToken },
  });
  if (error || !data.user) return { error: error?.message ?? 'signin_failed' };
  return { userId: data.user.id };
}

const GUEST_NAME_KEY = 'jr_private_live_guest_name';

export function rememberGuestName(name: string): void {
  try {
    localStorage.setItem(GUEST_NAME_KEY, name);
  } catch {
    /* sans mémoire */
  }
}

export function recallGuestName(): string {
  try {
    return localStorage.getItem(GUEST_NAME_KEY) ?? '';
  } catch {
    return '';
  }
}

// ── Fin d'un défi privé : avis et SKILLCARDS (migration 0722) ─────────────────

export interface PrivateLiveCard {
  id: string;
  title: string;
  rarity: string;
  color_theme: string;
  creature: unknown;
  levels: string[];
  /** Questions du défi qui portaient sur cette carte. */
  questions: number;
  /** Accès complet (achetée, donnée par un prof, abonnement, pass). */
  unlocked: boolean;
}

export interface PrivateLiveReview {
  stars: number;
  comment: string | null;
}

export interface PrivateLiveSkillcards {
  premium: boolean;
  review: PrivateLiveReview | null;
  cards: PrivateLiveCard[];
}

export const privateLiveSkillcardsKey = (challengeId: string) =>
  ['private-live-skillcards', challengeId] as const;

export function usePrivateLiveSkillcards(challengeId: string | null) {
  return useQuery({
    queryKey: privateLiveSkillcardsKey(challengeId ?? ''),
    enabled: !!challengeId,
    staleTime: 60_000,
    queryFn: async (): Promise<PrivateLiveSkillcards> => {
      const { data, error } = await db.rpc('get_private_live_skillcards', {
        p_challenge: challengeId,
      });
      if (error) throw new Error(error.message);
      const d = (data ?? {}) as Partial<PrivateLiveSkillcards>;
      return { premium: !!d.premium, review: d.review ?? null, cards: d.cards ?? [] };
    },
  });
}

export function useRatePrivateLive(challengeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { stars: number; comment?: string | null }) => {
      const { data, error } = await db.rpc('rate_private_live', {
        p_challenge: challengeId,
        p_stars: input.stars,
        p_comment: input.comment ?? null,
      });
      if (error) throw new Error(error.message);
      return data as PrivateLiveReview;
    },
    onSuccess: (review) =>
      qc.setQueryData<PrivateLiveSkillcards>(privateLiveSkillcardsKey(challengeId), (old) =>
        old ? { ...old, review } : old,
      ),
  });
}

/** SKILLCARDS à proposer en fin de défi : d'abord celles qu'on n'a pas encore. PUR. */
export function skillcardsToOffer(cards: readonly PrivateLiveCard[]): PrivateLiveCard[] {
  return [...cards].sort((a, b) => Number(a.unlocked) - Number(b.unlocked));
}

/** Petite phrase sous une note : « Génial ! », « Bien », etc. PUR. */
export function reviewLabel(stars: number): string {
  return ['', 'Pas terrible', 'Bof', 'Bien', 'Très bien', 'Génial !'][stars] ?? '';
}
